if (!customElements.get('candle-bundle-picker')) {
  customElements.define(
    'candle-bundle-picker',
    class CandleBundlePicker extends HTMLElement {
      connectedCallback() {
        this.data = JSON.parse(this.querySelector('[data-candle-bundle-data]').textContent);
        this.form = document.getElementById(this.dataset.formId);
        if (!this.form) return;

        this.idInput = this.form.querySelector('[name="id"]');
        this.submitButton = this.form.querySelector('[type="submit"]');
        this.submitLabel = this.submitButton && this.submitButton.querySelector('span');
        this.itemsContainer = this.querySelector('[data-items]');
        this.section = this.closest('section, .shopify-section') || document;
        // Dropdowns the shopper changed by hand; the scent pills no longer overwrite these.
        this.touched = new Set();

        const requested = new URLSearchParams(window.location.search).get('set');
        const requestedTier = requested && this.querySelector(`[name^="CandleBundleTier"][value="${requested}"]`);
        if (requestedTier) requestedTier.checked = true;
        if (!this.querySelector('[name^="CandleBundleTier"]:checked')) {
          const firstTier = this.querySelector('[name^="CandleBundleTier"]');
          if (firstTier) firstTier.checked = true;
        }
        if (!this.querySelector('[name^="CandleScent"]:checked')) {
          const firstScent = this.querySelector('[name^="CandleScent"]');
          if (firstScent) firstScent.checked = true;
        }

        this.addEventListener('change', this.onChange);
        this.bindStickyBar();
        this.applyScent();
        this.update();
      }

      get tier() {
        const checked = this.querySelector('[name^="CandleBundleTier"]:checked');
        return checked ? Number(checked.value) : 1;
      }

      get scent() {
        const checked = this.querySelector('[name^="CandleScent"]:checked');
        return checked ? checked.value : null;
      }

      selectsFor(tier) {
        return Array.from(this.querySelectorAll(`.candle-bundle__tier[data-tier="${tier}"] [data-candle-select]`));
      }

      scentsFor(tier) {
        return tier === 1 ? [this.scent] : this.selectsFor(tier).map((select) => select.value);
      }

      onChange = (event) => {
        const target = event.target;
        if (target.matches('[name^="CandleScent"]')) {
          this.applyScent();
          this.updateMedia();
        } else if (target.matches('[data-candle-select]')) {
          this.touched.add(target.id);
          // Candle 1 mirrors the scent pills, so changing it changes the headline scent too.
          if (target.dataset.index === '1') {
            const pill = Array.from(this.querySelectorAll('[name^="CandleScent"]')).find((input) => input.value === target.value);
            if (pill) {
              pill.checked = true;
              this.applyScent(false);
              this.updateMedia();
            }
          }
        }
        this.update();
      };

      // Presets every candle-1 dropdown, and (when cascading) any dropdown the shopper hasn't changed, to the chosen scent.
      applyScent(cascade = true) {
        const scent = this.scent;
        if (!scent) return;
        this.querySelectorAll('[data-candle-select]').forEach((select) => {
          const isFirst = select.dataset.index === '1';
          if (!isFirst && (!cascade || this.touched.has(select.id))) return;
          if (Array.from(select.options).some((option) => option.value === scent)) select.value = scent;
        });
      }

      // Candle lines for a tier: variant id -> { price, quantity }. One scent picked twice is one line.
      linesFor(tier) {
        const lines = new Map();
        this.scentsFor(tier).forEach((scent) => {
          const variant = this.data.variants[scent];
          if (!variant) return;
          const line = lines.get(variant.id) || { price: variant.price, quantity: 0 };
          line.quantity += 1;
          lines.set(variant.id, line);
        });
        return lines;
      }

      // Price of a tier in cents after its percentage discount. Shopify takes the percentage off each unit
      // and rounds that discount down to the penny (£39.99 less 10% is £36.00), so this does the same.
      tierTotal(tier) {
        const card = this.querySelector(`.candle-bundle__tier[data-tier="${tier}"]`);
        const percent = Number(card ? card.dataset.percent : 0);
        let total = 0;
        this.linesFor(tier).forEach(({ price, quantity }) => {
          total += (price - Math.floor((price * percent) / 100)) * quantity;
        });
        return total;
      }

      formatMoney(cents) {
        return FoxTheme.Currency.formatMoney(cents, this.data.moneyFormat);
      }

      update = () => {
        const tier = this.tier;
        const scent = this.scent;
        const single = this.data.variants[scent];

        this.querySelectorAll('.candle-bundle__tier').forEach((card) => {
          const cardTier = Number(card.dataset.tier);
          const selected = cardTier === tier;
          card.classList.toggle('is-selected', selected);
          const mix = card.querySelector('.candle-bundle__mix');
          if (mix) mix.hidden = !selected;

          const total = this.tierTotal(cardTier);
          const price = card.querySelector('[data-tier-price]');
          if (price) price.textContent = this.formatMoney(total);
          const each = card.querySelector('[data-tier-each]');
          if (each) each.textContent = this.formatMoney(Math.round(total / cardTier));
        });

        this.querySelectorAll('[data-selected-scent], [data-single-scent]').forEach((el) => (el.textContent = scent || ''));
        const note = this.querySelector('[data-scent-note]');
        const pill = this.querySelector('[name^="CandleScent"]:checked');
        if (note) note.textContent = (pill && pill.dataset.note) || '';

        const price = this.formatMoney(this.tierTotal(tier));
        const available = Boolean(single);
        this.writeItems(tier);

        const quantityInput = this.form.querySelector('[name="quantity"]') || document.querySelector(`[name="quantity"][form="${this.dataset.formId}"]`);
        const quantityWrapper = quantityInput && quantityInput.closest('.product-form__quantity');
        if (quantityInput) quantityInput.disabled = tier > 1;
        if (quantityWrapper) quantityWrapper.hidden = tier > 1;

        // Express checkout buttons can only buy the single variant, so they are hidden for sets.
        this.form.querySelectorAll('.shopify-payment-button').forEach((button) => (button.hidden = tier > 1));

        if (this.submitButton) {
          this.submitButton.disabled = !available;
          if (this.submitLabel) {
            this.submitLabel.textContent = available ? `${this.dataset.addLabel} · ${price}` : this.dataset.unavailableLabel;
          }
        }

        this.updatePrices(price);
      };

      // A set goes to the cart as ordinary candle lines; one scent picked twice becomes one line with quantity 2.
      writeItems(tier) {
        this.itemsContainer.innerHTML = '';
        if (tier === 1) {
          const single = this.data.variants[this.scent];
          if (single) this.idInput.value = single.id;
          this.idInput.disabled = false;
          return;
        }

        let index = 0;
        this.linesFor(tier).forEach(({ quantity }, id) => {
          [
            ['id', id],
            ['quantity', quantity],
          ].forEach(([field, value]) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = `items[${index}][${field}]`;
            input.value = value;
            input.setAttribute('form', this.dataset.formId);
            this.itemsContainer.appendChild(input);
          });
          index += 1;
        });
        this.idInput.disabled = true;
      }

      updateMedia() {
        const single = this.data.variants[this.scent];
        if (!single || !single.media) return;
        const gallery = document.querySelector(`[id^="MediaGallery-${this.dataset.sectionId}"]`);
        if (!gallery || typeof gallery.setActiveMedia !== 'function') return;
        if (typeof gallery.init === 'function') gallery.init();
        gallery.setActiveMedia({ featured_media: single.media });
      }

      updatePrices(price) {
        const priceBlock = this.section.querySelector('.product__block--price');
        if (priceBlock) {
          priceBlock.querySelectorAll('.f-price-item--regular').forEach((item) => (item.textContent = price));
        }

        const sticky = this.stickyBar;
        if (sticky) {
          sticky.querySelectorAll('.f-price-item--regular').forEach((item) => (item.textContent = price));
        }
      }

      bindStickyBar() {
        this.stickyBar = document.querySelector(`sticky-atc-bar[data-product-id="${this.dataset.productId}"]`);
        if (!this.stickyBar) return;

        // The sticky bar has its own single-variant select; hide it and send its button to the main form.
        const select = this.stickyBar.querySelector('select');
        const selectWrapper = select && (select.closest('.select') || select.parentElement);
        if (selectWrapper) selectWrapper.hidden = true;
        const stickyQuantity = this.stickyBar.querySelector('quantity-input');
        if (stickyQuantity) stickyQuantity.hidden = true;

        const stickyButton = this.stickyBar.querySelector('[type="submit"]');
        if (!stickyButton) return;
        stickyButton.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.scrollIntoView({ behavior: 'smooth', block: 'center' });
            if (this.submitButton && !this.submitButton.disabled) {
              setTimeout(() => this.form.requestSubmit(this.submitButton), 300);
            }
          },
          true
        );
      }
    }
  );
}
